import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import { Navigate, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import SignupShell from '../components/SignupShell';
import { billingApi, billingEnabled, money } from './api';
import { PaymentForm, SavedSetup } from './PaymentForms';

let stripePromise;
const selection = account => ({
  version_key: account.signup.selected_plan.version_key,
  consent_version: account.billing_consent.version, accept_billing_terms: true,
});
function stripeClient(livemode) {
  const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  if (!key?.startsWith(livemode == null ? 'pk_' : livemode ? 'pk_live_' : 'pk_test_')) {
    throw new Error('Payment setup is unavailable. Please contact support.');
  }
  if (!stripePromise) stripePromise = loadStripe(key);
  return stripePromise;
}
export default function BillingPayment() {
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [session, setSession] = useState(null);
  const [stripe, setStripe] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      setError('');
      const access = await billingApi.status();
      if (access.service_state === 'active') { navigate('/', { replace: true }); return; }
      if (access.service_state === 'plan_required') { navigate('/choose-plan', { replace: true }); return; }
      if (!access.can_complete_setup) { setError('Your firm’s owner needs to complete payment setup.'); return; }
      const value = await billingApi.account();
      if (!value.signup?.plan_choice_saved || !value.signup.selected_plan) { navigate('/billing-setup', { replace: true }); return; }
      // Resume only an already authorized setup; opening a new card form does
      // not record consent or lock the selected plan.
      if (value.pending_setup && value.signup.choice_locked) {
        const resumed = await billingApi.startSetup(selection(value));
        if (resumed.active) { navigate('/', { replace: true }); return; }
        setSession(resumed);
        if (resumed.status !== 'succeeded') setStripe(stripeClient(resumed.livemode));
      } else {
        setStripe(stripeClient());
      }
      setAccount(value);
    } catch (failure) { setError(failure.message); }
  }, [navigate]);
  useEffect(() => { if (billingEnabled && Cookies.get('token')) load(); }, [load]);
  const complete = async setupId => {
    await billingApi.completeSetup(setupId);
    navigate('/password-created', { replace: true, state: { billingSetup: true } });
  };
  const start = async () => {
    const result = await billingApi.startSetup(selection(account));
    if (result.active) navigate('/', { replace: true });
    else stripeClient(result.livemode);
    return result;
  };
  if (!billingEnabled) return <Navigate to="/" replace />;
  if (!Cookies.get('token') || !Cookies.get('agencyId')) return <Navigate to="/signin" replace />;
  return <SignupShell><Stack spacing={3}>
    <Box><Typography variant="body2" color="primary" fontWeight={600} mb={1}>Step 4 of 4</Typography>
      <Typography component="h1" fontSize={30} fontWeight={600} color="primary.heading">Add your payment method</Typography></Box>
    {error && <Alert severity="error" action={!account && <Button onClick={load}>Retry</Button>}>{error}</Alert>}
    {!account && !error && <CircularProgress aria-label="Loading payment setup" />}
    {account && <>
      <Box sx={{ p: 2.5, bgcolor: '#eef6ff', borderRadius: '12px' }}>
        <Typography fontWeight={600} color="primary.heading">{account.signup.selected_plan.display_name} · {money(account.signup.selected_plan.monthly_rate_cents)} per case per month</Typography>
      </Box>
      {session?.status === 'succeeded' ? <SavedSetup session={session} onComplete={complete} /> :
        <Elements stripe={stripe} options={session ? { clientSecret: session.client_secret }
          : { mode: 'setup', currency: account.currency, allowedPaymentMethodTypes: ['card'] }}>
          <PaymentForm session={session} onStart={start} onComplete={complete} onBusyChange={setBusy} />
        </Elements>}
      <Button onClick={() => navigate('/billing-setup')} disabled={busy}>Back to plan choice</Button>
    </>}
  </Stack></SignupShell>;
}
